import { useEffect, useState, useCallback, useMemo } from "react";
import { api } from "@/lib/api";
import { useYear } from "@/context/YearContext";
import { formatINR, MONTHS, formatDateTime } from "@/lib/format";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowDown, Plus, Trash2, Unlock, Pencil, Lock } from "lucide-react";

const num = (v) => (v === "" || v === null || isNaN(parseFloat(v)) ? 0 : parseFloat(v));

export default function Closing() {
  const { year } = useYear();
  const [closings, setClosings] = useState([]);
  const [categories, setCategories] = useState([]);
  const [month, setMonth] = useState("");
  const [editing, setEditing] = useState(null);
  const [f, setF] = useState({ revenue: "", gst: "", incomeTax: "", otherStat: "", tds: "", growth: "", notes: "" });
  const [lines, setLines] = useState([]);
  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const [cl, cats] = await Promise.all([
      api.get("/closings", { params: { year } }),
      api.get("/categories", { params: { include_archived: true } }),
    ]);
    setClosings(cl.data);
    setCategories(cats.data);
    const closed = cl.data.map((c) => c.month);
    const next = Array.from({ length: 12 }, (_, i) => i + 1).find((m) => !closed.includes(m));
    setMonth(next ? String(next) : "");
    setEditing(null);
  }, [year]);

  useEffect(() => { load(); }, [load]);

  const buildLines = useCallback(async (m) => {
    const { data: expenses } = await api.get("/expenses", { params: { year } });
    const catMap = Object.fromEntries(categories.map((c) => [c.id, c.name]));
    setLines(expenses.map((e) => ({
      expense_id: e.id, category_id: e.category_id, category_name: catMap[e.category_id] || "Unknown",
      name: e.name, budgeted: e.months[m - 1], actual: "", notes: "", unbudgeted: false,
    })));
  }, [year, categories]);

  useEffect(() => {
    if (month && !editing && categories.length) buildLines(parseInt(month));
  }, [month, editing, categories, buildLines]);

  const setLine = (idx, field, value) => setLines((prev) => prev.map((ln, i) => (i === idx ? { ...ln, [field]: value } : ln)));
  const addUnbudgeted = () => setLines((prev) => [...prev, {
    expense_id: null, category_id: categories[0]?.id || null, category_name: categories[0]?.name || "Unbudgeted",
    name: "", budgeted: 0, actual: "", notes: "", unbudgeted: true,
  }]);
  const removeLine = (idx) => setLines((prev) => prev.filter((_, i) => i !== idx));

  const lineIssue = (ln) => {
    const a = num(ln.actual);
    if (a < 0) return "Negative not allowed";
    if (ln.unbudgeted) {
      if (!ln.name.trim()) return "Name required";
      if (!ln.notes.trim()) return "Reason required for unbudgeted expense";
      return null;
    }
    if (ln.budgeted > 0) {
      const pct = Math.abs(a - ln.budgeted) / ln.budgeted * 100;
      if (pct > 10 && !ln.notes.trim()) return `Variance ${pct.toFixed(1)}% — explanation required`;
    } else if (a > 0 && !ln.notes.trim()) {
      return "Explanation required (no budget)";
    }
    return null;
  };

  const totals = useMemo(() => {
    const actual = lines.reduce((s, ln) => s + num(ln.actual), 0);
    const taxes = num(f.gst) + num(f.incomeTax) + num(f.otherStat);
    return { actual, taxes, net: num(f.revenue) - taxes };
  }, [lines, f]);

  const negatives = [f.revenue, f.gst, f.incomeTax, f.otherStat, f.tds, f.growth].some((v) => num(v) < 0);
  const lineErrors = lines.filter((ln) => lineIssue(ln)).length;
  const blocked = !month || f.revenue === "" || negatives || lineErrors > 0 || totals.taxes > num(f.revenue);

  const payload = useCallback(() => ({
    year, month: parseInt(month), revenue: num(f.revenue),
    gst_payable: num(f.gst), income_tax_provision: num(f.incomeTax), other_statutory: num(f.otherStat),
    tds_credit: num(f.tds), growth_expenses: num(f.growth), notes: f.notes,
    expense_lines: lines.filter((ln) => !ln.unbudgeted || ln.name.trim()).map((ln) => ({
      expense_id: ln.expense_id, category_id: ln.category_id, category_name: ln.category_name,
      name: ln.name, budgeted: ln.budgeted, actual: num(ln.actual), notes: ln.notes, unbudgeted: ln.unbudgeted,
    })),
  }), [year, month, f, lines]);

  useEffect(() => {
    const run = async () => {
      if (!month || f.revenue === "" || negatives) { setPreview(null); setPreviewError(""); return; }
      try {
        const { data } = await api.post("/closings/preview", payload());
        setPreview(data); setPreviewError("");
      } catch (e) {
        setPreview(null); setPreviewError(e.response?.data?.detail || "Preview failed");
      }
    };
    const t = setTimeout(run, 400);
    return () => clearTimeout(t);
  }, [payload, month, f.revenue, negatives]);

  const submit = async () => {
    if (blocked) { toast.error("Fix the highlighted issues before closing the month"); return; }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/closings/${editing.id}`, payload());
        toast.success(`${MONTHS[parseInt(month) - 1]} ${year} re-closed (v${editing.version + 1})`);
      } else {
        await api.post("/closings", payload());
        toast.success(`${MONTHS[parseInt(month) - 1]} ${year} closed & locked`);
      }
      setF({ revenue: "", gst: "", incomeTax: "", otherStat: "", tds: "", growth: "", notes: "" });
      setPreview(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Closing failed");
    } finally { setSaving(false); }
  };

  const reopen = async (c) => {
    const reason = window.prompt(`Admin reopen — a written reason is mandatory.\nWhy are you reopening ${MONTHS[c.month - 1]} ${c.year}?`);
    if (reason === null) return;
    if (!reason.trim()) { toast.error("Reason is mandatory"); return; }
    try {
      await api.post(`/closings/${c.id}/reopen`, { reason });
      toast.success("Month reopened — edit and re-close it");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Reopen failed"); }
  };

  const loadForEdit = (c) => {
    setEditing(c);
    setMonth(String(c.month));
    setF({ revenue: String(c.revenue), gst: String(c.gst_payable || 0), incomeTax: String(c.income_tax_provision || 0),
           otherStat: String(c.other_statutory || 0), tds: String(c.tds_credit || 0), growth: String(c.growth_expenses), notes: c.notes || "" });
    setLines((c.expense_lines || []).map((ln) => ({ ...ln, actual: String(ln.actual) })));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const closedMonths = closings.map((c) => c.month);
  const grouped = useMemo(() => {
    const g = {};
    lines.forEach((ln, idx) => {
      const key = ln.unbudgeted ? "— Unbudgeted —" : ln.category_name;
      (g[key] = g[key] || []).push({ ...ln, idx });
    });
    return g;
  }, [lines]);

  return (
    <div data-testid="closing-form-container" className="space-y-8">
      <div className="fade-up flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-zinc-900">Monthly Closing</h1>
          <p className="text-sm text-zinc-500 mt-1">Taxes set aside first → waterfall on net allocable cash → month locks on closing</p>
        </div>
        {editing && (
          <div className="flex items-center gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="editing-banner">
            <Pencil size={14} /> Editing reopened month: {MONTHS[editing.month - 1]} {editing.year}
            <button className="underline ml-1" onClick={() => { setEditing(null); setF({ revenue: "", gst: "", incomeTax: "", otherStat: "", tds: "", growth: "", notes: "" }); load(); }}>Cancel</button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div className="xl:col-span-2 space-y-6">
          <div className="card-surface p-6 fade-up space-y-4">
            <p className="text-xs font-semibold uppercase tracking-widest text-zinc-500">Cash & Taxes</p>
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <label className="text-xs uppercase tracking-wider text-zinc-500 mb-1.5 block">Month</label>
                <Select value={month} onValueChange={setMonth} disabled={!!editing}>
                  <SelectTrigger data-testid="closing-month-select" className="bg-[#F8F7F3] border-[#E3E0D8]"><SelectValue placeholder="Month" /></SelectTrigger>
                  <SelectContent>
                    {MONTHS.map((m, i) => (
                      <SelectItem key={m} value={String(i + 1)} disabled={!editing && closedMonths.includes(i + 1)}>
                        {m} {closedMonths.includes(i + 1) ? "· closed" : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Field label="Cash Revenue Received (₹)" testId="waterfall-revenue-input" value={f.revenue} onChange={(v) => setF({ ...f, revenue: v })} />
              <Field label="GST / Indirect Tax Payable (₹)" testId="closing-gst-input" value={f.gst} onChange={(v) => setF({ ...f, gst: v })} />
              <Field label="Income-Tax Provision (₹)" testId="closing-incometax-input" value={f.incomeTax} onChange={(v) => setF({ ...f, incomeTax: v })} />
              <Field label="Other Statutory (₹)" testId="closing-statutory-input" value={f.otherStat} onChange={(v) => setF({ ...f, otherStat: v })} />
              <Field label="TDS Credit (reference only)" testId="closing-tds-input" value={f.tds} onChange={(v) => setF({ ...f, tds: v })} />
            </div>
            <div className="flex flex-wrap gap-x-8 gap-y-1 text-sm pt-2 border-t border-zinc-100">
              <span className="text-zinc-500">Taxes total: <span className="font-mono-num font-semibold text-rose-600">{formatINR(totals.taxes)}</span></span>
              <span className="text-zinc-500">Net allocable cash: <span className="font-mono-num font-semibold text-zinc-900" data-testid="net-allocable-value">{formatINR(Math.max(0, totals.net))}</span></span>
              {totals.taxes > num(f.revenue) && f.revenue !== "" && <span className="text-red-600 text-xs" data-testid="taxes-exceed-error">Taxes cannot exceed revenue</span>}
            </div>
          </div>

          <div className="card-surface fade-up overflow-hidden" data-testid="actuals-sheet">
            <div className="px-6 pt-5 pb-3 flex flex-wrap items-center justify-between gap-2">
              <div>
                <p className="text-xs font-semibold uppercase tracking-widest text-zinc-500">Category-wise Actual Expenses {month && `· ${MONTHS[parseInt(month) - 1]}`}</p>
                <p className="text-[11px] text-zinc-400 mt-0.5">Variance beyond 10% and unbudgeted expenses require a written reason</p>
              </div>
              <Button data-testid="add-unbudgeted-btn" variant="outline" size="sm" className="border-[#E3E0D8] bg-white text-zinc-700" onClick={addUnbudgeted}>
                <Plus size={13} className="mr-1" /> Unbudgeted Expense
              </Button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[860px]">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-wider text-zinc-400 border-b border-zinc-100">
                    <th className="px-6 py-2.5 font-medium min-w-[190px]">Expense</th>
                    <th className="px-2 py-2.5 font-medium text-right">Budgeted</th>
                    <th className="px-2 py-2.5 font-medium text-right">Actual</th>
                    <th className="px-2 py-2.5 font-medium text-right">Variance</th>
                    <th className="px-2 py-2.5 font-medium text-right">Var %</th>
                    <th className="px-2 py-2.5 font-medium min-w-[200px]">Notes / Reason</th>
                    <th className="px-3 py-2.5 w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {lines.length === 0 && (
                    <tr><td colSpan={7} className="px-6 py-8 text-center text-zinc-500">No budgeted expenses for this year. Add expenses in the Budget tab first.</td></tr>
                  )}
                  {Object.entries(grouped).map(([cat, rows]) => (
                    <CategoryRows key={cat} cat={cat} rows={rows} setLine={setLine} removeLine={removeLine} lineIssue={lineIssue} categories={categories} />
                  ))}
                </tbody>
                {lines.length > 0 && (
                  <tfoot>
                    <tr className="border-t-2 border-blue-600/30 bg-[#FCFBF8]">
                      <td className="px-6 py-3 font-heading font-semibold text-zinc-900">Total Actual Recurring Expenses</td>
                      <td className="px-2 py-3 text-right font-mono-num text-xs text-zinc-500">{formatINR(lines.reduce((s, l) => s + l.budgeted, 0))}</td>
                      <td className="px-2 py-3 text-right font-mono-num text-sm font-bold text-blue-600" data-testid="actual-expenses-total">{formatINR(totals.actual)}</td>
                      <td colSpan={4} className="px-2 py-3 text-xs text-zinc-400">Deducted from Operating Reserve on closing</td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>

          <div className="card-surface p-6 fade-up space-y-4">
            <p className="text-xs font-semibold uppercase tracking-widest text-zinc-500">Growth & Notes</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Growth Expenses (from I&G fund only)" testId="closing-growth-expenses-input" value={f.growth} onChange={(v) => setF({ ...f, growth: v })} />
              <div>
                <label className="text-xs uppercase tracking-wider text-zinc-500 mb-1.5 block">Closing Notes</label>
                <Textarea data-testid="closing-notes-input" rows={2} className="bg-[#F8F7F3] border-[#E3E0D8]" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} />
              </div>
            </div>
            {previewError && <p className="text-xs text-red-600" data-testid="closing-preview-error">{previewError}</p>}
            {lineErrors > 0 && <p className="text-xs text-red-600" data-testid="line-errors-warning">{lineErrors} expense line(s) need attention before closing.</p>}
            <Button data-testidx="submit" data-testid="waterfall-submit-btn" className="w-full bg-blue-600 hover:bg-blue-500 text-white font-semibold"
              onClick={submit} disabled={saving || blocked || !!previewError}>
              <Lock size={14} className="mr-2" />
              {saving ? "Closing…" : editing ? `Re-close ${MONTHS[parseInt(month || 1) - 1]} (creates audit version)` : "Close Month & Lock"}
            </Button>
          </div>
        </div>

        <div className="card-surface p-6 fade-up h-fit sticky top-24" data-testid="waterfall-preview-card">
          <p className="text-xs font-semibold uppercase tracking-widest text-zinc-500 mb-4">Live Waterfall Preview</p>
          {!preview ? (
            <p className="text-sm text-zinc-500">Enter revenue to see the distribution of net allocable cash.</p>
          ) : (
            <div className="space-y-1">
              <Step label="Cash revenue" value={num(f.revenue)} color="#18181B" bold />
              <Step label="− Taxes & statutory" value={preview.taxes_total} color="#F43F5E" sub="Added to Taxes & Statutory Reserve" />
              <Step label="Net allocable cash" value={preview.net_allocable} color="#18181B" bold />
              <Arrow />
              <Step label={`1 · Next-Year Reserve (due ${formatINR(preview.reserve_due)})`} value={preview.reserve_contribution} color="#059669"
                sub={preview.carried_shortfall_before > 0 ? `Includes carried shortfall ${formatINR(preview.carried_shortfall_before)}` : `Monthly target ${formatINR(preview.monthly_target)}`} />
              {preview.reserve_shortfall_after > 0 && (
                <p className="text-xs text-red-600 pl-1 py-1" data-testid="preview-shortfall-warning">
                  ⚠ Shortfall {formatINR(preview.reserve_shortfall_after)} carried forward — Founder & Growth stay ₹0
                </p>
              )}
              <Arrow />
              <Step label="2 · Founder Family Savings (30%)" value={preview.founder_allocation} color="#D97706" />
              <Step label="3 · Innovation & Growth (70%)" value={preview.growth_allocation} color="#7C3AED" />
              <Arrow />
              <Step label="Growth expenses" value={num(f.growth)} color="#DC2626"
                sub={`Available I&G: ${formatINR(preview.available_growth_balance)} (carry ${formatINR(preview.prev_growth_carry)})`} />
              <div className="mt-3 pt-3 border-t border-zinc-100 flex items-center justify-between">
                <span className="text-sm font-medium text-zinc-800">Unspent I&G carried forward</span>
                <span className="font-mono-num font-bold text-violet-600" data-testid="preview-unspent-balance">{formatINR(preview.unspent_growth_balance)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-zinc-500">Operating Reserve impact</span>
                <span className="font-mono-num text-sm font-semibold text-red-600">−{formatINR(preview.actual_expenses)}</span>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="card-surface fade-up overflow-hidden" data-testid="closings-history-table">
        <div className="px-6 pt-6 pb-3">
          <p className="text-xs font-semibold uppercase tracking-widest text-zinc-500">Closing History · {year}</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[1000px]">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wider text-zinc-400 border-b border-zinc-100">
                <th className="px-6 py-3 font-medium">Month</th>
                <th className="px-3 py-3 font-medium">Status</th>
                <th className="px-3 py-3 font-medium text-right">Revenue</th>
                <th className="px-3 py-3 font-medium text-right">Taxes</th>
                <th className="px-3 py-3 font-medium text-right">Expenses</th>
                <th className="px-3 py-3 font-medium text-right">Reserve</th>
                <th className="px-3 py-3 font-medium text-right">Founder</th>
                <th className="px-3 py-3 font-medium text-right">Unspent I&G</th>
                <th className="px-4 py-3 font-medium">Closed At</th>
                <th className="px-3 py-3 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {closings.length === 0 && <tr><td colSpan={10} className="px-6 py-8 text-center text-zinc-500">No closings for {year}</td></tr>}
              {closings.map((c) => (
                <tr key={c.id} className="border-b border-zinc-100 last:border-0 hover:bg-zinc-50">
                  <td className="px-6 py-3 font-medium text-zinc-800">{MONTHS[c.month - 1]}
                    {c.notes && <p className="text-[11px] text-zinc-400 max-w-[130px] truncate">{c.notes}</p>}
                  </td>
                  <td className="px-3 py-3">
                    {c.status === "reopened" ? (
                      <span className="text-[10px] uppercase font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">Reopened</span>
                    ) : (
                      <span className="text-[10px] uppercase font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">
                        Closed{c.version > 1 ? ` v${c.version}` : ""}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-3 text-right font-mono-num">{formatINR(c.revenue)}</td>
                  <td className="px-3 py-3 text-right font-mono-num text-rose-600">{formatINR(c.taxes_total)}</td>
                  <td className="px-3 py-3 text-right font-mono-num text-zinc-500">{formatINR(c.actual_expenses)}</td>
                  <td className="px-3 py-3 text-right font-mono-num text-emerald-600">{formatINR(c.reserve_contribution)}</td>
                  <td className="px-3 py-3 text-right font-mono-num text-amber-600">{formatINR(c.founder_allocation)}</td>
                  <td className="px-3 py-3 text-right font-mono-num font-semibold text-zinc-900">{formatINR(c.unspent_growth_balance)}</td>
                  <td className="px-4 py-3 text-xs text-zinc-400">{formatDateTime(c.created_at)}</td>
                  <td className="px-3 py-3">
                    {c.status === "closed" ? (
                      <button data-testid={`closing-reopen-${c.id}`} onClick={() => reopen(c)}
                        className="flex items-center gap-1 text-xs text-zinc-500 hover:text-amber-700" title="Admin reopen (reason required)">
                        <Unlock size={12} /> Reopen
                      </button>
                    ) : (
                      <button data-testid={`closing-edit-${c.id}`} onClick={() => loadForEdit(c)}
                        className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-500 font-medium">
                        <Pencil size={12} /> Edit & Re-close
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

const Field = ({ label, testId, value, onChange }) => {
  const invalid = value !== "" && parseFloat(value) < 0;
  return (
    <div>
      <label className="text-xs uppercase tracking-wider text-zinc-500 mb-1.5 block">{label}</label>
      <Input data-testid={testId} type="number" min="0" placeholder="0"
        className={`bg-[#F8F7F3] border-[#E3E0D8] font-mono-num ${invalid ? "border-red-400" : ""}`}
        value={value} onChange={(e) => onChange(e.target.value)} />
      {invalid && <p className="text-[11px] text-red-600 mt-1">Negative values are not allowed</p>}
    </div>
  );
};

const CategoryRows = ({ cat, rows, setLine, removeLine, lineIssue, categories }) => (
  <>
    <tr className="bg-[#F4F2EC]">
      <td colSpan={7} className="px-6 py-1.5 text-[11px] font-semibold uppercase tracking-widest text-blue-600">{cat}</td>
    </tr>
    {rows.map((ln) => {
      const a = num(ln.actual);
      const variance = a - ln.budgeted;
      const pct = ln.budgeted > 0 ? (variance / ln.budgeted) * 100 : (a > 0 ? 100 : 0);
      const issue = lineIssue(ln);
      return (
        <tr key={ln.idx} className={`border-b border-zinc-100 ${issue ? "bg-red-50/40" : ""}`}>
          <td className="px-6 py-2">
            {ln.unbudgeted ? (
              <div className="flex gap-1.5">
                <select className="text-xs bg-white border border-[#E3E0D8] rounded px-1 py-1 text-zinc-700" value={ln.category_id || ""}
                  data-testid="unbudgeted-category-select"
                  onChange={(e) => { const c = categories.find((x) => x.id === e.target.value); setLine(ln.idx, "category_id", e.target.value); setLine(ln.idx, "category_name", c?.name || ""); }}>
                  {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                <input data-testid="unbudgeted-name-input" placeholder="Expense name" value={ln.name}
                  onChange={(e) => setLine(ln.idx, "name", e.target.value)}
                  className="text-[13px] bg-white border border-[#E3E0D8] rounded px-2 py-1 w-32 text-zinc-800 outline-none focus:border-blue-500" />
              </div>
            ) : (
              <p className="font-medium text-zinc-800 text-[13px]">{ln.name}</p>
            )}
          </td>
          <td className="px-2 py-2 text-right font-mono-num text-xs text-zinc-500">{formatINR(ln.budgeted)}</td>
          <td className="px-2 py-2 text-right">
            <input data-testid="actual-expense-input" type="number" min="0" placeholder="0" value={ln.actual}
              onChange={(e) => setLine(ln.idx, "actual", e.target.value)}
              className="w-24 bg-white border border-[#E3E0D8] focus:border-blue-500 rounded px-1.5 py-1 text-right font-mono-num text-xs text-zinc-800 outline-none" />
          </td>
          <td className={`px-2 py-2 text-right font-mono-num text-xs ${variance > 0 ? "text-red-600" : variance < 0 ? "text-emerald-600" : "text-zinc-400"}`}>
            {a || ln.budgeted ? (variance >= 0 ? "+" : "−") + formatINR(Math.abs(variance)) : "—"}
          </td>
          <td className={`px-2 py-2 text-right font-mono-num text-xs ${Math.abs(pct) > 10 ? "font-bold text-red-600" : "text-zinc-400"}`}>
            {ln.budgeted > 0 || a > 0 ? `${pct.toFixed(1)}%` : "—"}
          </td>
          <td className="px-2 py-2">
            <input data-testid="line-notes-input" placeholder={issue ? issue : "Optional"} value={ln.notes}
              onChange={(e) => setLine(ln.idx, "notes", e.target.value)}
              className={`w-full bg-white border rounded px-2 py-1 text-xs text-zinc-700 outline-none focus:border-blue-500 ${issue ? "border-red-300 placeholder:text-red-400" : "border-[#E3E0D8]"}`} />
          </td>
          <td className="px-3 py-2">
            {ln.unbudgeted && (
              <button data-testid="remove-unbudgeted-btn" onClick={() => removeLine(ln.idx)} className="text-zinc-400 hover:text-red-600 p-1"><Trash2 size={12} /></button>
            )}
          </td>
        </tr>
      );
    })}
  </>
);

const Step = ({ label, value, color, sub, bold }) => (
  <div className="flex items-start justify-between py-1.5">
    <div>
      <span className={`text-sm ${bold ? "font-semibold text-zinc-900" : "text-zinc-500"}`}>{label}</span>
      {sub && <p className="text-[11px] text-zinc-400">{sub}</p>}
    </div>
    <span className="font-mono-num text-sm font-semibold whitespace-nowrap pl-3" style={{ color }}>{formatINR(Math.abs(value))}</span>
  </div>
);

const Arrow = () => <div className="flex justify-center py-0.5"><ArrowDown size={12} className="text-zinc-300" /></div>;
